'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import { Loader2, ArrowLeft, CheckCircle, Mail } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';
import { sendPasswordResetEmail } from '@/lib/email/emailService';

interface ForgotPasswordFormData {
  email: string;
}

export default function ForgotPasswordScreen() {
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState('');

  const { register, handleSubmit, formState: { errors } } = useForm<ForgotPasswordFormData>();

  const onSubmit = async (data: ForgotPasswordFormData) => {
    setIsLoading(true);
    try {
      const supabase = createClient();
      const siteUrl = process.env.NEXT_PUBLIC_SITE_URL ?? 'https://fanfinite.com';
      const redirectTo = `${siteUrl}/sign-up-login-screen`;

      const { error } = await supabase.auth.resetPasswordForEmail(data.email, {
        redirectTo,
      });

      if (error) throw error;

      // Send branded password reset email via Resend
      await sendPasswordResetEmail({
        to: data.email,
        resetLink: redirectTo,
      });

      setSubmittedEmail(data.email);
      setIsSuccess(true);
    } catch (err: unknown) {
      console.error('Password reset error:', err);
      // Still show success to avoid email enumeration
      setSubmittedEmail(data.email);
      setIsSuccess(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6 py-12 relative">
      {/* Background glow */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[400px] h-[400px] rounded-full opacity-6"
          style={{ background: 'radial-gradient(circle, #F5C518 0%, transparent 70%)' }} />
      </div>

      <div className="relative z-10 w-full max-w-md">
        {/* Logo */}
        <Link href="/" className="flex items-center justify-center gap-2.5 mb-10">
          <AppLogo size={36} />
          <span className="font-bold text-lg text-foreground">FanFinite</span>
        </Link>

        <div className="bg-card border border-border rounded-2xl p-8 shadow-2xl">
          {!isSuccess ? (
            <>
              <div className="flex flex-col items-center text-center mb-8">
                <div className="w-14 h-14 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center mb-4">
                  <Mail size={24} className="text-primary" />
                </div>
                <h1 className="text-2xl font-bold text-foreground mb-2">Reset Your Password</h1>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Enter your account email and we&apos;ll send you a secure reset link. 
                  Check your inbox — even your spam folder, Detective.
                </p>
              </div>

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
                <div>
                  <label htmlFor="reset-email" className="block text-sm font-medium text-foreground mb-1.5">
                    Email Address
                  </label>
                  <input
                    id="reset-email"
                    type="email"
                    placeholder="your@email.com"
                    {...register('email', {
                      required: 'Email address is required',
                      pattern: { value: /^[^@]+@[^@]+\.[^@]+$/, message: 'Enter a valid email address' }
                    })}
                    className="w-full px-4 py-3 bg-input border border-border rounded-xl text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-all duration-150"
                  />
                  {errors.email && (
                    <p className="mt-1.5 text-xs text-negative">{errors.email.message}</p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full py-3.5 bg-primary text-primary-foreground text-sm font-bold rounded-xl hover:bg-primary/90 disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-150 active:scale-[0.98] flex items-center justify-center gap-2"
                  style={{ minHeight: '48px' }}
                >
                  {isLoading ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      <span>Sending reset link...</span>
                    </>
                  ) : (
                    'Send Reset Link'
                  )}
                </button>
              </form>

              <div className="mt-6 pt-6 border-t border-border text-center">
                <Link
                  href="/sign-up-login-screen"
                  className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors duration-150"
                >
                  <ArrowLeft size={14} />
                  Back to Sign In
                </Link>
              </div>
            </>
          ) : (
            /* Success state */
            <div className="flex flex-col items-center text-center py-4">
              <div className="w-16 h-16 rounded-2xl bg-positive/10 border border-positive/20 flex items-center justify-center mb-5">
                <CheckCircle size={28} className="text-positive" />
              </div>
              <h2 className="text-xl font-bold text-foreground mb-3">Check Your Inbox</h2>
              <p className="text-sm text-muted-foreground leading-relaxed mb-2">
                We sent a password reset link to:
              </p>
              <p className="text-sm font-semibold text-primary font-mono-data mb-6">{submittedEmail}</p>
              <p className="text-xs text-muted-foreground mb-8 max-w-xs">
                The link expires in 1 hour. If you don&apos;t see it, check your spam folder or try resending.
              </p>

              <div className="space-y-3 w-full">
                <button
                  onClick={() => setIsSuccess(false)}
                  className="w-full py-3 border border-border text-foreground text-sm font-semibold rounded-xl hover:border-primary/50 hover:bg-primary/5 transition-all duration-150"
                >
                  Resend Reset Link
                </button>
                <Link
                  href="/sign-up-login-screen"
                  className="block w-full py-3 text-center text-sm font-semibold text-primary hover:text-primary/80 transition-colors"
                >
                  Back to Sign In
                </Link>
              </div>
            </div>
          )}
        </div>

        <p className="text-center text-xs text-muted-foreground mt-6">
          New to FanFinite?{' '}
          <Link href="/sign-up-login-screen" className="text-primary hover:text-primary/80 transition-colors font-semibold">
            Create your Detective ID
          </Link>
        </p>
      </div>
    </div>
  );
}